/** Date Range Types */

export type DatePreset =
  | 'today'
  | 'yesterday'
  | 'last7days'
  | 'last30days'
  | 'thisMonth'
  | 'custom';

export interface DateRange {
  from: Date | null;
  to: Date | null;
  preset: DatePreset;
}

/** Filter Types */

export interface DashboardFilters {
  searchQuery: string;
  dateRange: DateRange;
  sdrIds: string[];
  listTypes: ContactListType[];
  orgIds: string[];
  /**
   * Session statuses to keep. Empty means every status, which is the default.
   *
   * Exists for the Active Sessions tile: that KPI ignores the date range on
   * purpose — "active" is a real-time fact, and a session paused three weeks
   * ago is still paused — so the list it opens has to be able to ignore it too.
   */
  statuses: string[];
}

/** started OR paused: exactly what the Active Sessions KPI counts. */
export const ACTIVE_SESSION_STATUSES = ['started', 'paused'];

export type ContactListType = 'CSV' | 'HUBSPOT' | 'CUSTOM_HUBSPOT' | 'OUTREACH' | 'SALESFORCE' | 'FOURMATIV';

/** Session Types */

export interface DashboardSession {
  id: string;
  shortId: string;
  sdrId: string;
  sdrName?: string;
  sdrProfilePicture?: string;
  startAtSession: string | { $date: string };
  updatedAtSession: string | { $date: string };
  listNames: string;
  /** Ids of the lists the session was built from, in the same order as `listNames`. */
  contactListIds?: string[];
  type: ContactListType;
  totalContacts: number;
  organizationId: string;
  organizationName?: string;
  status: SessionStatus;
  totalCalls?: number;
  connectRate?: number;
  appointmentsSet?: number;
}

/** Metrics Types (calculated from sessions) */

export interface DashboardMetrics {
  totalSessions: number;
  totalContacts: number;
}

/** API Response Types */

export interface ApiError {
  message: string;
  raw?: string;
}

export type ApiResponse<T> =
  | { success: true; data: T; totalPages?: number; totalCount?: number; pageNumber?: number }
    | { success: true; data: T; totalPages?: number; totalCount?: number; pageNumber?: number; totalStatusCreated?: number; totalStatusSuccess?: number; totalStatusError?: number }
  | { success: false; error: ApiError };

export interface PaginatedResponse<T> {
  data: T[];
  pageNumber: number;
  totalPages: number;
  totalCount: number;
}

export interface PaginatedWithMetricsResponse<T> {
  data: T[];
  pageNumber: number;
  totalPages: number;
  totalCount: number;
  totalStatusCreated: number;
  totalStatusSuccess: number;
  totalStatusError: number;
}

/** Session Details Types */

export type CallAction =
  | 'called'
  | 'transferred'
  | 'voicemail'
  | 'answered'
  | 'not_answered'
  | 'call_ended'
  | 'cut_off'
  | 'transfer_error';

export type CallDisposition =
  | 'refuse_transfer'
  | 'bad_number_busy_signal'
  | 'opt_out'
  | 'left_vm'
  | 'phone_refusal'
  | 'gatekeeper_conversation'
  | 'referred_another_contact'
  | 'no_longer_with_company'
  | 'set_appointment'
  | 'wrong_number'
  | 'phone_tree_no_connection'
  | 'no_answer'
  | 'decision_maker_conversation'
  | 'other'
  | 'sdr_did_not_pick_up'
  | 'transferred_without_disposition'
  | 'default_action'
  | 'TND'
  | 'DA'
  | 'LVM';

export interface SessionDetailsCall {
  action: CallAction;
  contactId: string;
  contactName: string;
  contactListId: string;
  contactListName: string;
  callId: string;
  disposition: CallDisposition | null;
  existRecordings: boolean;
  createdAt: string;
  agentId?: string;
  agentName?: string;
  flagged: boolean;
}

/** Contact Info (from /contacts/{id} endpoint) */

export interface ContactInfo {
  id: string;
  name: string;
  phone: string;
  email: string;
  jobTitle: string;
  accountName: string;
  notes?: string;
  notesSDR?: string;
  notesAgent?: string;
  crmId?: string;
  contactsListId: string;
  flagged?: boolean;
}

export enum SessionStatus {
  FINISHED = 'finished',
  PAUSED = 'paused',
  STARTED = 'started',
}

export interface SessionDetailsInfo {
  callSessionId: string;
  sdrId: string;
  sdrName: string;
  sdrEmail?: string;
  sdrProfilePicture?: string;
  organizationId: string;
  organizationName: string;
  status: SessionStatus;
  startAtSession: string;
  updatedAtSession: string;
  listType: ContactListType;
  totalContacts: number;
}

export interface SessionDetails {
  session: SessionDetailsInfo;
  calls: SessionDetailsCall[];
  /** Every agent who worked the session — session-wide, not page-wide. */
  agents?: Array<{ id: string; name: string }>;
  /**
   * The action values this session's rows actually carry — session-wide.
   *
   * Not the same as the metric event counts: those include `called`,
   * `cut_off` and `call_ended`, which no row can ever show, so offering them
   * in the filter emptied the table.
   */
  actions?: string[];
}

/**
 * Filters for the session's calls list. Applied by the backend inside Mongo, so
 * a filtered view costs one page of rows instead of the whole session.
 */
export type CallsTabFilter = 'all' | 'connected' | 'meetings' | 'recordings';

export interface SessionCallsFilters {
  search?: string;
  /** Long form or abbreviation — the backend canonicalizes it. */
  disposition?: string;
  action?: string;
  agentId?: string;
  tab?: CallsTabFilter;
}

/**
 * Contact counts for a session. Three different questions, deliberately kept
 * apart — showing the list's totals as the session's is what made the header
 * report 112 contacts for a session that called 36.
 */
export interface SessionContactSummary {
  /** Distinct contacts this session actually dialled. */
  contactsCalled: number;
  /** Flagged contacts among the ones it dialled. */
  fuegoLeads: number;
  /** How many contacts the session's contact list(s) hold. */
  listContacts: number;
}

/** One step of the path a call took. */
export interface CallPathEvent {
  action: string;
  /** Only meaningful on the last event — the SDR classifies when the call ends. */
  disposition: string | null;
  at: string;
  /** Seconds since the first event of the path. */
  offsetSeconds: number;
}

export interface ContactTimeline {
  events: CallPathEvent[];
  durationSeconds: number;
  /** Calls this contact received in this session. More than one is 4.2% of cases. */
  dialCount: number;
  /**
   * Which of those calls this path is, 1-based. `null` when the path was not
   * scoped to a single call and therefore spans all of them.
   */
  dialIndex: number | null;
  /** The path hit the server's event ceiling; there are more events than shown. */
  truncated: boolean;
}

/** Session Stats (computed from calls) */

export interface SessionStats {
  // Call Flow Stats
  totalCalls: number;
  /**
   * Calls that resulted in a real human conversation, computed by the backend
   * from the call dispositions.
   *
   * This replaces the old `answered`, which was a raw count of the
   * `action="answered"` metric. That action is written whenever a call reaches
   * the leg — voicemail pickups and wrong numbers included — so it reported 37
   * out of 36 calls (102.8%) on a session where only 17 were conversations.
   */
  connected: number;
  /** Distinct contacts — matches the number of rows in the calls table. */
  uniqueContacts: number;
  /** Calls the SDR never classified. */
  unclassified: number;
  transferred: number;

  // Dial to Connect
  dialToConnectRatio: number;

  // Disposition Stats
  gatekeeperConversation: number;
  leftVoicemail: number;
  wrongNumber: number;
  phoneTreeNoConnection: number;
  noLongerWithCompany: number;
  referredAnotherContact: number;
  decisionMakerConversation: number;
  optOut: number;
  noAnswer: number;
  badNumberBusySignal: number;
  phoneRefusal: number;
  refuseTransfer: number;

  // Key Metrics
  setAppointments: number;

  /**
   * Session-wide counts, used to populate the filter dropdowns. They must NOT
   * come from the loaded rows: with server-side pagination those are one page,
   * so each dropdown would only offer what happens to be on screen.
   */
  dispositionCounts: Record<string, number>;
}

/** Recording Types */

export interface CallRecording {
  id: string;
  twilioRecordingId: string;
  recordingUrl: string;
  duration: string;
  label: 'SDR' | 'AGENT';
}

export interface SessionRecordings {
  sdrCallRecording?: CallRecording;
  agentCallRecording?: CallRecording;
}

/** SDR Info (for name lookup) */

export interface SdrInfo {
  id: string;
  name: string;
  email?: string;
}

/** Organization Info (for name lookup) */

export interface OrganizationInfo {
  id: string;
  name: string;
}

/** Backend Metrics Types (from /metrics endpoints) */

export interface DispositionMetrics {
  refuse_transfer: number;
  bad_number_busy_signal: number;
  opt_out: number;
  left_vm: number;
  phone_refusal: number;
  gatekeeper_conversation: number;
  referred_another_contact: number;
  no_longer_with_company: number;
  set_appointment: number;
  wrong_number: number;
  phone_tree_no_connection: number;
  no_answer: number;
  decision_maker_conversation: number;
  other: number;
  sdr_did_not_pick_up: number;
  default_action: number;
  transferred_without_disposition: number;
}

// Combined build + disposition metrics returned by the single summary endpoint
// (replaces two separate requests).
/**
 * Everything the session detail needs, already computed by the backend from the
 * same canonical source the overview uses. Nothing here should be recomputed in
 * the browser — that is what let the two views disagree.
 */
export interface SessionMetricsSummary {
  /** callLogs in the session. One callLog = one call. */
  totalCalls: number;
  /** Distinct contacts — the number of rows in the calls table. */
  uniqueContacts: number;
  /** Calls whose disposition is a real human conversation. */
  connected: number;
  transferredCalls: number;
  /** transferredCalls / totalCalls, as a percentage. */
  connectRate: number;
  /** Calls the SDR never classified. */
  unclassified: number;
  dispositionMetrics: DispositionMetrics;
}

export interface IntegrationJob {
  _id: string;
  organizationId: string;
  contactListId: string;
  nonce: string;
  attempt: number;
  status: string;
  createdAt: {
    $date: string;
  };
  updatedAt: {
    $date: string;
  };
  logs: [];
}

export interface Job {
  _id: string;
  organizationId: string;
  contactListId: string;
  nonce: string;
  attempt: number;
  status: string;
  createdAt: string;
  deleted: null;
  updatedAt: string;
}

export enum JobStatus {
  CREATED = 'CREATED',
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR',
  PROCESSING = 'PROCESSING',
  CONFIRMED = 'CONFIRMED',
}

/** UI State Types */

/** Helper Functions */

export type LoadingState = 'IDLE' | 'LOADING' | 'SUCCESS' | 'ERROR';

export function getDateRangeFromPreset (preset: DatePreset): { from: Date; to: Date } {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  switch (preset) {
  case 'today':
    return { from: today, to: now };
  case 'yesterday': {
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    return { from: yesterday, to: today };
  }
  case 'last7days': {
    const last7 = new Date(today);
    last7.setDate(last7.getDate() - 7);
    return { from: last7, to: now };
  }
  case 'last30days': {
    const last30 = new Date(today);
    last30.setDate(last30.getDate() - 30);
    return { from: last30, to: now };
  }
  case 'thisMonth':
    return { from: new Date(now.getFullYear(), now.getMonth(), 1), to: now };
  default:
    return { from: today, to: now };
  }
}

export const DEFAULT_FILTERS: DashboardFilters = {
  searchQuery: '',
  dateRange: {
    from: null,
    to: null,
    preset: 'last7days',
  },
  sdrIds: [],
  listTypes: [],
  orgIds: [],
  statuses: [],
};
